// @flow
/**
 * @fileoverview Library of utility methods for numbers.
 */
/* eslint no-restricted-properties:0 */

/**
 * Round a number with decimals.
 *
 * All browsers offer their implementation of this (with toPrecision, and
 * toFixed), however they don't have the same behavior. This implementation
 * ensure the value displayed will be the same in all browsers.
 *
 * number The number to truncate.
 * precision The number of decimals
 * returns The number with the appropriate number of decimals.
 */
export function quantize(number: number, precision: number): number {
  let value = number * Math.pow(10, precision);
  let fraction = Math.round((value - parseInt(value, 10)) * 10) / 10;

  // Internet explorer round numbers differently: if the value is
  // -1.5, it will round to: -1.0 instead of -2. So moving all -0.5
  // into -0.6.
  if (fraction === -0.5) {
    fraction = -0.6;
  }

  value = Math.round(parseInt(value, 10) + fraction) / Math.pow(10, precision);
  return value;
}

/**
 * Convert a number to a currency format
 *  val The number ( 80 )
 * returns '80.00'
 */
export function formatAsCurrency(val: string): string {
  const floatValue = parseFloat(val);
  const num = quantize(floatValue, 2);
  return num ? num.toFixed(2) : val;
}

/**
 * Convert an angle from degrees to radians
 *
 * degrees The angle in degrees.
 * returns The angle in radians.
 */
export function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

/**
 * Convert a currency value from standard units of measure to micro units.
 *
 * value The currency value in standard units (dollars, pounds, etc.).
 * returns The currency value in micro units (microdollars, micropounds, etc.).
 */
export function currencyToMicroCurrency(
  value: number,
  hasFractionalCents: boolean = false,
): number {
  let roundingFactor = 100;
  let scalingFactor = 10000;
  if (hasFractionalCents) {
    roundingFactor = 1000;
    scalingFactor = 1000;
  }
  return Math.round(value * roundingFactor) * scalingFactor;
}

/**
 * Convert a currency value from micro units to standard units of measure.
 *
 * value The currency value in micro units of measure (microdollars, micropounds, etc.).
 * returns The value in standard units of measure (dollars, pounds, etc.).
 */
export function microCurrencyToCurrency(
  value: number,
  hasFractionalCents: boolean = false,
): number {
  let dividingFactor = 1000000.0;
  if (hasFractionalCents) {
    dividingFactor = 1000000.0;
  }
  return value / dividingFactor;
}

/** Converts to a currency format like formatAsCurrency, but expects microcurrency input */
export function formatMicroCurrencyAsCurrency(value: number) {
  return value ? formatAsCurrency(microCurrencyToCurrency(value).toString()) : '';
}

/**
 * Parses the input string into a valid microCurrency value
 * @param {string} value
 */
export function parseCurrencyToMicroCurrency(value: string) {
  const numericalValue = parseFloat(value);
  let parsedValue = 0;
  if (!isNaN(numericalValue)) {
    parsedValue = numericalValue * 1000000;
  }

  return parsedValue;
}

const localeProperties = {
  fractionalCentFormatData: {},
  formatData: {},
};

function regexEscape(unescapedString: string) {
  if (unescapedString) {
    unescapedString = unescapedString.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');
  }
  return unescapedString;
}

function getFormatData(locale: string, hasFractionalCents: boolean = false) {
  const formatDataKey = hasFractionalCents ? 'fractionalCentFormatData' : 'formatData';
  let formatData = localeProperties[formatDataKey][locale];
  if (!formatData) {
    let minimumFractionDigits = 2;
    let maximumFractionDigits = 2;
    // -9999.99 is used to pick out the digit group separator, a decimal separator, and a sign indicator
    let numberFormat = -9999.99;
    if (hasFractionalCents) {
      minimumFractionDigits = 3;
      maximumFractionDigits = 3;
      numberFormat = -9999.999;
    }
    const format = new IntlPolyfill.NumberFormat(locale, {
      minimumFractionDigits,
      maximumFractionDigits,
    });

    const formatParts = format.formatToParts(numberFormat);
    const minusSignPart = formatParts.find((part) => part.type === 'minusSign');
    const groupPart = formatParts.find((part) => part.type === 'group');
    const decimalPart = formatParts.find((part) => part.type === 'decimal');
    const groupDelimeter = groupPart ? groupPart.value : '';
    const escapedGroupDelimeter = regexEscape(groupDelimeter);
    const decimalDelimiter = decimalPart ? decimalPart.value : '';
    const cleanerString =
      '[^0-9' +
      escapedGroupDelimeter +
      regexEscape(decimalDelimiter) +
      regexEscape(minusSignPart ? minusSignPart.value : '') +
      ']';

    formatData = {
      format,
      decimal: decimalPart ? decimalPart.value : null,
      group: groupDelimeter,
      groupRegex: new RegExp(escapedGroupDelimeter || '', 'g'),
      cleaner: new RegExp(cleanerString, 'g'),
    };

    localeProperties[formatDataKey][locale] = formatData;
  }
  return formatData;
}

export function parseNumberBasedOnLocale(
  number: ?string,
  locale: string,
  hasFractionalCents: boolean = false,
): number {
  if (typeof number === 'number') {
    return number;
  }

  if (typeof number !== 'string') {
    return NaN;
  }

  const formatData = getFormatData(locale, hasFractionalCents);
  const decimalSeparatorPlaceHolder = '!_!';

  let processedString = number.replace(formatData.cleaner, '');

  if (formatData.decimal) {
    // use a place holder that will be replaced by . later
    processedString = processedString.replace(formatData.decimal, decimalSeparatorPlaceHolder);
  }
  if (formatData.group) {
    processedString = processedString.replace(formatData.groupRegex, '');
  }

  processedString = processedString.replace(decimalSeparatorPlaceHolder, '.');
  return parseFloat(processedString);
}

export function formatBasedOnLocale(
  value: number,
  locale: string,
  hasFractionalCents: boolean = false,
): string {
  const formatData = getFormatData(locale, hasFractionalCents);
  return formatData.format.format(value);
}

/**
 * Format a currency number as USD.
 *
 * E.g.
 *   NumberUtil.formatUSD(1.10) = 1.10
 *   NumberUtil.formatUSD(1.01) = 1.01
 *   NumberUtil.formatUSD(1.0001) = 1.00
 *   NumberUtil.formatUSD(1000.1) = 1,000.10
 *   NumberUtil.formatUSD(1000000.1) = 1,000,000.10
 *
 * value The currency number.
 * returns The formatted string.
 */
export function formatUSD(value: number): string {
  return value.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
}

export default {
  quantize,
  formatAsCurrency,
  toRadians,
  currencyToMicroCurrency,
  microCurrencyToCurrency,
  parseNumberBasedOnLocale,
  formatBasedOnLocale,
  formatUSD,
};
